import { TransactionCategory } from './transaction';
import { maximumScoreValue } from './scores';
import { AsyncStatus } from './resource-fetching-status';
import { CountryCode } from '@algoan/deep-dive/countries';

export type WidgetId =
  | 'personal-loan-analysis'
  | 'debt-collection-synthesis'
  | 'cofidis-debt-collection-synthesis'
  | 'debt-consolidation-synthesis'
  | 'cofidis-debt-consolidation-synthesis'
  | 'bank-history'
  | 'expenditure-chart'
  | 'regular-cash-flows-analysis'
  | 'debt-consolidation-loan-analysis'
  | 'score'
  | 'invitation'
  | 'activity-score'
  | 'balances';

// if true, the widget is visible. Otherwise, it is not.
export type WidgetConfig = Partial<Record<WidgetId, boolean>>;

interface CommonPersonalData {
  path: string;
  label: string;
}

interface EnumPersonalData extends CommonPersonalData {
  type: 'enum';
  values: { [key: string]: string | undefined };
}

interface OtherPersonalData extends CommonPersonalData {
  type: 'string' | 'number' | 'boolean' | 'date';
}

export type PersonalData = EnumPersonalData | OtherPersonalData;

export interface ColumnConfig {
  path: string;
  label: string;
}

export interface ApplicationTableConfig {
  additionalColumns?: ColumnConfig[];
  //The following property represents paths to fields in the customer model
  filterByColumns?: string[];
}

export enum TeamColor {
  GREEN = 'green',
  TURQUOISE = 'turquoise',
  BLUE = 'blue',
  PURPLE = 'purple',
  PINK = 'pink',
  RED = 'red',
  ORANGE = 'orange',
  YELLOW = 'yellow',
  MUSTARD_YELLOW = 'mustardYellow',
  VIOLET = 'violet',
}

export interface TeamConfig {
  teams: TeamInformation[];
}

export interface TeamInformation {
  name: string;
  color: TeamColor;
}

export interface StringParam {
  name: string;
  description: string;
  type?: 'string';
  required?: boolean;
  isCustomIdentifier?: boolean;
}

export interface ListParam {
  name: string;
  description: string;
  type: 'list';
  options: string[];
  required?: boolean;
}

export type LinkParam = StringParam | ListParam;

export interface Link {
  url: string;
  params: LinkParam[];
}

export interface IdentityPanel {
  title: string;
  personalData: PersonalData[];
}

export interface ScoreRange {
  min: number;
  max: number;
  startColor: string;
  endColor: string;
  label?: string;
}

export interface ScoreConfig {
  ranges: ScoreRange[];
}

export interface Recipient {
  name: string;
  projectId: string;
  organizationId: string;
}

export interface ShareConfig {
  recipients: Recipient[];
}

export const defaultScoreConfig: ScoreConfig = {
  ranges: [
    { startColor: '#F9232E', endColor: '#DF5705' }, // red
    { startColor: '#EF8906', endColor: '#FDB408' }, // orange
    { startColor: '#FFCE00', endColor: '#E5CD0B' }, // yellow
    { startColor: '#88E14F', endColor: '#0BDC9D' }, // light green
    { startColor: '#07C675', endColor: '#03B468' }, // green
  ].map(
    (range, index, ranges): ScoreRange => ({
      ...range,
      min: maximumScoreValue * (index / ranges.length),
      max: maximumScoreValue * ((index + 1) / ranges.length),
    }),
  ),
};

export interface DashboardConfig {
  widgets: WidgetConfig;
  identityExtraInformation?:
    | [IdentityPanel]
    | [IdentityPanel, IdentityPanel]
    | [IdentityPanel, IdentityPanel, IdentityPanel]
    | [IdentityPanel, IdentityPanel, IdentityPanel, IdentityPanel];
  minimumNumberOfTransactionsToDisplayFrequentTransactions?: number;
  applicationTable?: ApplicationTableConfig;
  teamConfig?: TeamConfig;
  link?: Link;
  cashFlowCategoriesChecked?: TransactionCategory[];
  scoreConfig?: ScoreConfig;
  shareConfig?: ShareConfig;
  isRecategorizationEnabled?: boolean;
  countryCode?: CountryCode;
}

interface ProjectConfiguration {
  aggregation: {
    enableRefresh: boolean;
  };
  computing?: {
    currency?: string;
  };
}

export enum ProjectProductsName {
  CREDIT_INSIGHTS = 'CREDIT_INSIGHTS',
  CREDIT_SCORE = 'CREDIT_SCORE',
  PAYMENT_SCORE = 'PAYMENT_SCORE',
  DASHBOARD = 'DASHBOARD',
  BANK_DATA = 'BANK_DATA',
}

interface ProjectProducts {
  name: ProjectProductsName;
}

export interface ProjectConfig {
  id: string;
  configuration: ProjectConfiguration;
  products: ProjectProducts[];
  name: string;
}

export interface VersionedConfig extends DashboardConfig {
  author: string;
  createdAt: string;
  id: string;
  version: number;
}

export interface CreditInsightsConfig {
  computing?: ComputingConfiguration;
}

export interface TransactionDataConfig {
  smsTemplatePerLanguage: Record<string, string>;
}

export interface ComputingConfiguration {
  window?: WindowConfiguration;
}

export interface WindowConfiguration {
  mode?: WindowConfigurationMode;
  /**
   * The length of the window in days necessary in DAYS configuration
   */
  numberOfDays?: number;
  /**
   * The length of the window in months necessary in all not DAYS configuration
   */
  numberOfMonths?: number;
  includeCheckingAccountsOnTruncateWindow?: boolean;
}

export enum WindowConfigurationMode {
  DAYS = 'DAYS',
  MONTHS = 'MONTHS',
  MONTHS_TRUNCATED = 'MONTHS_TRUNCATED',
  MONTHS_AND_CURRENT = 'MONTHS_AND_CURRENT',
}

export interface ConfigState {
  configFetchingStatus: AsyncStatus;
  error: boolean;
  config?: {
    dashboard: DashboardConfig;
    creditInsights: CreditInsightsConfig | undefined;
    transactionDataConfig: TransactionDataConfig | undefined;
    projectConfig: ProjectConfig | undefined;
  };
}
